/**
 * Generic per-(chain, token_address) exchange rate composable.
 *
 * Reads the latest USD price from the public.exchange_rates table and
 * subscribes to realtime UPDATE events so the value stays fresh without
 * any third-party oracle calls from the browser.
 *
 * Module-level state is intentional: each (chain, tokenAddress) pair gets a
 * single shared subscription across all components that observe it.
 */
import { ref, readonly, type Ref } from 'vue'
import type { SupabaseClient, RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import { reportFallback, trackSupabaseChannelStatus } from '~/utils/reconnectTelemetry'
import { readTtlCacheEntry, writeTtlCache, clearTtlCacheByPrefix } from '~/utils/ttlCache'

// Exchange rates are public reference data refreshed every ~5 min by the
// `price-snapshot` edge function, so a 5-min cache window matches the source
// cadence exactly. Within the window we read from cache instead of the REST
// endpoint; a live realtime subscription keeps the value fresher than this.
const PRICE_CACHE_TTL_MS = 5 * 60 * 1000
const CACHE_PREFIX = 'aurumrun:rate:'

function cacheKey(chain: string, tokenAddressLower: string): string {
  return `${CACHE_PREFIX}${chain}:${tokenAddressLower}`
}

interface ExchangeRateRow {
  chain: string
  token_address: string
  symbol: string
  quote: string
  price: string | number
  source: string
  staleness: string
  captured_at: string
  updated_at: string
}

interface PairState {
  price: Ref<number>
  isLoading: Ref<boolean>
  error: Ref<string | null>
  lastFetchedAt: Ref<number | null>
  channel: RealtimeChannel | null
  tabHiddenAt: number
  visibilityHandler: (() => void) | null
  /** localStorage key used to persist the latest price across reloads/tabs. */
  cacheKey: string
}

const pairs = new Map<string, PairState>()

// Resolved once per module load. `undefined` = not yet resolved (try to bind);
// `null` = SSR or test context with no $supabase.
let supabaseClient: SupabaseClient | null | undefined = undefined

function getSupabase(): SupabaseClient | null {
  if (supabaseClient !== undefined) return supabaseClient
  try {
    const { $supabase } = useNuxtApp() as { $supabase?: SupabaseClient }
    supabaseClient = $supabase ?? null
  } catch {
    supabaseClient = null
  }
  return supabaseClient
}

function pairKey(chain: string, tokenAddress: string): string {
  return `${chain}:${tokenAddress.toLowerCase()}`
}

function getOrCreateState(chain: string, tokenAddress: string, fallback: number): PairState {
  const tokenAddressLower = tokenAddress.toLowerCase()
  const key = pairKey(chain, tokenAddressLower)
  let state = pairs.get(key)
  if (!state) {
    // Seed from the persisted cache so the very first paint after a reload (and
    // any new tab) shows the last known price without an immediate REST read.
    // `seedAt` preserves the original write time so the TTL window is measured
    // from when the value was cached, not from now.
    const ck = cacheKey(chain, tokenAddressLower)
    const cached = readTtlCacheEntry<number>(ck, PRICE_CACHE_TTL_MS)
    let seedPrice = fallback
    let seedAt: number | null = null
    if (cached && Number.isFinite(cached.value) && cached.value > 0) {
      seedPrice = cached.value
      seedAt = cached.cachedAt
    }
    state = {
      price: ref<number>(seedPrice),
      isLoading: ref<boolean>(false),
      error: ref<string | null>(null),
      lastFetchedAt: ref<number | null>(seedAt),
      channel: null,
      tabHiddenAt: 0,
      visibilityHandler: null,
      cacheKey: ck,
    }
    pairs.set(key, state)
  }
  return state
}

/** True while the last read (REST or realtime tick) is still within the TTL. */
function isCacheFresh(state: PairState): boolean {
  return (
    state.lastFetchedAt.value !== null &&
    Date.now() - state.lastFetchedAt.value < PRICE_CACHE_TTL_MS
  )
}

/** Update price refs only when the value actually changed — avoids churning
 *  Vue reactivity in 30+ subscribed components on every realtime tick. */
function applyPrice(state: PairState, num: number): void {
  if (!Number.isFinite(num) || num <= 0) return
  if (state.price.value === num) return
  state.price.value = num
  state.lastFetchedAt.value = Date.now()
  // Persist the freshest value (realtime ticks land here too) so reloads and
  // other tabs start from it.
  writeTtlCache(state.cacheKey, num)
}

async function fetchLatest(
  supabase: SupabaseClient,
  state: PairState,
  chain: string,
  tokenAddressLower: string,
  force = false,
): Promise<void> {
  // Skip the REST read while the cached value is still within its TTL. Realtime
  // UPDATE events also refresh `lastFetchedAt` (via applyPrice), so an active
  // subscription keeps this guard satisfied and the endpoint stays idle.
  if (!force && isCacheFresh(state)) return
  state.isLoading.value = true
  if (state.error.value !== null) state.error.value = null
  try {
    const { data, error } = await supabase
      .from('exchange_rates')
      .select('price, captured_at')
      .eq('chain', chain)
      .eq('token_address', tokenAddressLower)
      .eq('quote', 'USD')
      .maybeSingle()

    if (error) throw error
    if (data) {
      const num = typeof data.price === 'string' ? parseFloat(data.price) : (data.price as number)
      applyPrice(state, num)
      // Refresh the TTL window on every successful read, even when the value is
      // unchanged (applyPrice short-circuits the equal-value case).
      state.lastFetchedAt.value = Date.now()
      if (Number.isFinite(num) && num > 0) writeTtlCache(state.cacheKey, num)
    }
  } catch (e) {
    state.error.value = e instanceof Error ? e.message : 'Failed to fetch exchange rate'
    console.error('[useExchangeRate] fetch error:', e)
  } finally {
    state.isLoading.value = false
  }
}

function applyPayload(
  state: PairState,
  chain: string,
  payload: RealtimePostgresChangesPayload<ExchangeRateRow>,
): void {
  const row = payload.new as ExchangeRateRow | undefined
  if (!row) return
  // postgres_changes filter is single-column (token_address); guard chain in code
  // because the same address can appear on multiple chains (e.g. native gas sentinel).
  if (row.chain !== chain) return
  if (row.quote && row.quote !== 'USD') return
  const num = typeof row.price === 'string' ? parseFloat(row.price) : row.price
  applyPrice(state, num)
}

function setupVisibilityHandler(
  state: PairState,
  supabase: SupabaseClient,
  chain: string,
  tokenAddressLower: string,
): void {
  if (state.visibilityHandler || typeof document === 'undefined') return
  state.visibilityHandler = () => {
    if (document.hidden) {
      state.tabHiddenAt = Date.now()
    } else {
      const hidden = state.tabHiddenAt ? Date.now() - state.tabHiddenAt : 0
      state.tabHiddenAt = 0
      // Compensate for any UPDATE events missed while the tab was backgrounded —
      // but only when the cached value has actually gone stale, so the telemetry
      // stays aligned with real REST reads (the fetch would no-op otherwise).
      if (hidden > 5000 && !isCacheFresh(state)) {
        reportFallback('price', 'tab_refetch', 'tab_return', { hiddenMs: hidden })
        fetchLatest(supabase, state, chain, tokenAddressLower)
      }
    }
  }
  document.addEventListener('visibilitychange', state.visibilityHandler)
}

function ensureSubscribed(
  state: PairState,
  supabase: SupabaseClient,
  chain: string,
  tokenAddressLower: string,
): void {
  if (state.channel) return
  const channelName = `exchange-rates-${chain}-${tokenAddressLower}`
  state.channel = supabase
    .channel(channelName)
    .on<ExchangeRateRow>(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'exchange_rates',
        filter: `token_address=eq.${tokenAddressLower}`,
      },
      (payload: RealtimePostgresChangesPayload<ExchangeRateRow>) => {
        applyPayload(state, chain, payload)
      },
    )
    .subscribe((status, err) => {
      if (status === 'CHANNEL_ERROR') {
        console.error('[useExchangeRate] channel error:', err?.message || 'unknown')
      }
      trackSupabaseChannelStatus('price', status, err?.message)
    })
}

/**
 * Subscribe to and read the latest USD price for a (chain, tokenAddress) pair.
 *
 * @param chain         Chain slug used by the writer (e.g. 'ethereum').
 * @param tokenAddress  Token contract address. Normalized to lowercase internally.
 * @param fallback      Value to display until the first successful read.
 */
export function useExchangeRate(chain: string, tokenAddress: string, fallback: number) {
  const tokenAddressLower = tokenAddress.toLowerCase()
  const state = getOrCreateState(chain, tokenAddressLower, fallback)
  const supabase = getSupabase()

  if (supabase) {
    // First call for this pair triggers the initial fetch + subscription.
    // Subsequent calls hit the early-return guards inside ensureSubscribed
    // and setupVisibilityHandler, so this is a no-op after pair init.
    if (state.lastFetchedAt.value === null && !state.isLoading.value) {
      fetchLatest(supabase, state, chain, tokenAddressLower)
    }
    ensureSubscribed(state, supabase, chain, tokenAddressLower)
    setupVisibilityHandler(state, supabase, chain, tokenAddressLower)
  }

  /** Re-read the latest price. Respects the TTL cache unless `force` is true. */
  async function refresh(force = false): Promise<void> {
    if (!supabase) return
    await fetchLatest(supabase, state, chain, tokenAddressLower, force)
  }

  return {
    price: readonly(state.price),
    isLoading: readonly(state.isLoading),
    error: readonly(state.error),
    lastFetchedAt: readonly(state.lastFetchedAt),
    refresh,
  }
}

// Test-only — allow specs to reset module state between cases.
export function __resetExchangeRateStateForTests(): void {
  for (const state of pairs.values()) {
    if (state.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', state.visibilityHandler)
    }
  }
  pairs.clear()
  clearTtlCacheByPrefix(CACHE_PREFIX)
  supabaseClient = undefined
}
